// ============================================================================
// AiDataPanel — "What did AI see?" panel
// ============================================================================

import React, { useMemo, useState } from 'react';
import { normalizeFieldName, buildFieldAliasMap, resolveFieldName } from '@process-advisor/process-miner/consultant/attributePrivacyUtils';

const PANEL_STYLE = {
  margin: '0 10px 0',
  border: '1px solid #DFE1E6',
  borderTop: 'none',
  borderRadius: '0 0 8px 8px',
  background: '#fff',
  overflow: 'hidden',
};

const PANEL_TABS = ['Privacy map', 'Sample items sent to AI'];

export default function AiDataPanel({
  isOpen,
  onClose,
  // Function props (preferred) — computed fresh on each render when panel is open
  getTaskRows = null,       // () => taskJSON()
  getPrivacyRules = null,   // () => getAttributePrivacyRules()
  applySampleItemPrivacyFn = null,  // applySampleItemPrivacy function
  // Fallback array props (used when function props not provided)
  excludedFields: excludedFieldsProp = [],
  anonymizedFields: anonymizedFieldsProp = [],
  anonymizationMap: anonymizationMapProp = {},
  sampleItems: sampleItemsProp = [],
  privacyRules: privacyRulesProp = {},
  totalItems = 0,
}) {
  const [activePane, setActivePane] = useState('Privacy map');

  // Compute privacy fresh on every render when panel is open and function props are provided
  // This avoids all useEffect/state timing issues
  const computed = useMemo(() => {
    if (!isOpen || !getTaskRows || !getPrivacyRules || !applySampleItemPrivacyFn) return null;
    try {
      const rows = getTaskRows();
      const rules = getPrivacyRules();
      console.log('[AiDataPanel] computing privacy — rows:', rows.length, 'rules:', JSON.stringify(rules));
      const result = applySampleItemPrivacyFn(rows, rules);
      console.log('[AiDataPanel] result — excludedFields:', result.excludedFields, 'anonymizedFields:', result.anonymizedFields);
      if (result.rows.length > 0) console.log('[AiDataPanel] first row:', JSON.stringify(result.rows[0]));
      return result;
    } catch (e) {
      console.error('[AiDataPanel] privacy compute error:', e);
      return null;
    }
  }, [isOpen, getTaskRows, getPrivacyRules, applySampleItemPrivacyFn]);

  // Use computed result if available, otherwise fall back to props
  const excludedFields   = computed ? computed.excludedFields   : excludedFieldsProp;
  const anonymizedFields = computed ? computed.anonymizedFields  : anonymizedFieldsProp;
  const anonymizationMap = computed ? computed.anonymizationMap  : anonymizationMapProp;
  const sampleItems      = computed ? computed.rows              : sampleItemsProp;
  const privacyRules     = computed ? (getPrivacyRules ? getPrivacyRules() : privacyRulesProp) : privacyRulesProp;

  const columnInfo = useMemo(() => {
    const orderedColumns = [];
    const seen = new Set();

    (sampleItems || []).forEach((row) => {
      Object.keys(row || {}).forEach((key) => {
        if (!seen.has(key)) {
          seen.add(key);
          orderedColumns.push(key);
        }
      });
    });

    const aliasMap = buildFieldAliasMap([
      ...orderedColumns,
      ...Object.keys(privacyRules || {}),
      ...excludedFields,
      ...anonymizedFields,
      ...Object.keys(anonymizationMap || {}),
    ]);

    const effectivePrivacyRules = {};
    Object.entries(privacyRules || {}).forEach(([field, rule]) => {
      const resolvedField = resolveFieldName(field, aliasMap) || field;
      if (!effectivePrivacyRules[resolvedField]) {
        effectivePrivacyRules[resolvedField] = rule;
      }
    });

    const effectiveExcludedFields = [];
    (excludedFields || []).forEach((field) => {
      const resolvedField = resolveFieldName(field, aliasMap) || field;
      if (!effectiveExcludedFields.includes(resolvedField)) {
        effectiveExcludedFields.push(resolvedField);
      }
      if (!effectivePrivacyRules[resolvedField]) {
        effectivePrivacyRules[resolvedField] = 'exclude';
      }
    });

    const effectiveAnonymizedFields = [];
    (anonymizedFields || []).forEach((field) => {
      const resolvedField = resolveFieldName(field, aliasMap) || field;
      if (!effectiveAnonymizedFields.includes(resolvedField)) {
        effectiveAnonymizedFields.push(resolvedField);
      }
      if (!effectivePrivacyRules[resolvedField]) {
        effectivePrivacyRules[resolvedField] = 'anonymize';
      }
    });

    const effectiveAnonymizationMap = {};
    Object.entries(anonymizationMap || {}).forEach(([field, legend]) => {
      const resolvedField = resolveFieldName(field, aliasMap) || field;
      effectiveAnonymizationMap[resolvedField] = legend;
    });

    const normalColumns = orderedColumns.filter((col) => !effectivePrivacyRules[col]);
    const anonymizedColumns = orderedColumns.filter((col) => effectivePrivacyRules[col] === 'anonymize');
    const excludedColumns = [
      ...orderedColumns.filter((col) => effectivePrivacyRules[col] === 'exclude'),
      ...effectiveExcludedFields.filter((field) => !orderedColumns.includes(field)),
    ].filter((col, idx, arr) => arr.indexOf(col) === idx);

    return {
      sortedColumns: [...normalColumns, ...anonymizedColumns, ...excludedColumns],
      privacyRules: effectivePrivacyRules,
      excludedFields: effectiveExcludedFields,
      anonymizedFields: effectiveAnonymizedFields,
      anonymizationMap: effectiveAnonymizationMap,
    };
  }, [sampleItems, privacyRules, excludedFields, anonymizedFields, anonymizationMap]);

  if (!isOpen) return null;

  const {
    sortedColumns,
    privacyRules: effectivePrivacyRules,
    excludedFields: effectiveExcludedFields,
    anonymizedFields: effectiveAnonymizedFields,
    anonymizationMap: effectiveAnonymizationMap,
  } = columnInfo;

  const hasPrivacy = effectiveExcludedFields.length > 0 || effectiveAnonymizedFields.length > 0;

  const colHeader = (col) => {
    const rule = effectivePrivacyRules[col];
    if (rule === 'exclude') return {
      bg: '#FFF0EC', color: '#BF2600', badge: 'EXCL', badgeBg: '#FFEBE6', badgeColor: '#BF2600',
    };
    if (rule === 'anonymize') return {
      bg: '#EEF4FF', color: '#0052CC', badge: 'ANON', badgeBg: '#DDEEFF', badgeColor: '#0052CC',
    };
    return { bg: '#F4F5F7', color: '#42526E', badge: null };
  };

  const cellVal = (row, col) => {
    const rule = effectivePrivacyRules[col];
    if (rule === 'exclude') return { val: '—', style: { color: '#C1C7D0', fontStyle: 'italic' } };
    if (rule === 'anonymize') return { val: row[col] ?? '—', style: { color: '#0052CC', fontStyle: 'italic' } };
    const v = row[col];
    if (v === null || v === undefined || v === '') return { val: '—', style: { color: '#C1C7D0' } };
    const str = String(v);
    return { val: str.length > 30 ? str.slice(0, 30) + '…' : str, style: {} };
  };

  return (
    <div style={PANEL_STYLE}>
      <div style={{ display: 'flex', borderBottom: '1px solid #DFE1E6', background: '#FAFBFC' }}>
        {PANEL_TABS.map(tab => (
          <div
            key={tab}
            onClick={() => setActivePane(tab)}
            style={{
              padding: '8px 16px',
              fontSize: 12,
              fontWeight: 500,
              cursor: 'pointer',
              color: activePane === tab ? '#0052CC' : '#5E6C84',
              borderBottom: activePane === tab ? '2px solid #0052CC' : '2px solid transparent',
              whiteSpace: 'nowrap',
            }}
          >
            {tab === 'Privacy map' ? '🔒 ' : '📋 '}{tab}
          </div>
        ))}
        <div
          onClick={onClose}
          style={{ marginLeft: 'auto', padding: '8px 14px', fontSize: 18, color: '#5E6C84', cursor: 'pointer', lineHeight: 1.2 }}
        >
          ×
        </div>
      </div>

      {activePane === 'Privacy map' && (
        <div style={{ padding: '16px 20px' }}>
          {!hasPrivacy ? (
            <div style={{ fontSize: 13, color: '#5E6C84', padding: '24px 0', textAlign: 'center' }}>
              No privacy rules applied to this model.
            </div>
          ) : (
            <>
              {/* ── Summary stats row ── */}
              <div style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
                {[
                  { label: 'Fields excluded', value: effectiveExcludedFields.length, color: '#BF2600', bg: '#FFF0EC', border: '#FFBDAD' },
                  { label: 'Fields anonymized', value: effectiveAnonymizedFields.length, color: '#0052CC', bg: '#EEF4FF', border: '#B3D4FF' },
                  { label: 'Sample items sent', value: sampleItems.length, color: '#006644', bg: '#E3FCEF', border: '#ABF5D1' },
                ].map(({ label, value, color, bg, border }) => (
                  <div key={label} style={{ background: bg, border: `1px solid ${border}`, borderRadius: 6, padding: '10px 16px', minWidth: 130 }}>
                    <div style={{ fontSize: 24, fontWeight: 700, color, lineHeight: 1.1 }}>{value}</div>
                    <div style={{ fontSize: 11, color, marginTop: 3, fontWeight: 500 }}>{label}</div>
                  </div>
                ))}
                <div style={{ marginLeft: 'auto', fontSize: 12, color: '#5E6C84', alignSelf: 'center', maxWidth: 320, lineHeight: 1.5 }}>
                  These rules were applied when building the AI prompt. Switch to <strong style={{ color: '#0052CC' }}>Sample items</strong> to see the exact rows sent.
                </div>
              </div>

              {/* ── Excluded fields ── */}
              {effectiveExcludedFields.length > 0 && (
                <div style={{ marginBottom: 20 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                    <div style={{ width: 3, height: 16, background: '#DE350B', borderRadius: 2 }} />
                    <span style={{ fontSize: 12, fontWeight: 600, color: '#172B4D' }}>Excluded from AI</span>
                    <span style={{ fontSize: 11, color: '#5E6C84' }}>— these fields were stripped before sending. AI has no knowledge of them.</span>
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {effectiveExcludedFields.map(f => (
                      <span key={f} style={{
                        background: '#FFEBE6', color: '#BF2600', border: '1px solid #FF8F73',
                        borderRadius: 4, padding: '4px 12px', fontSize: 13, fontWeight: 500,
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                      }}>
                        <span style={{ fontSize: 12 }}>⊘</span> {f}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* ── Anonymized fields ── */}
              {effectiveAnonymizedFields.map(field => {
                const legend = effectiveAnonymizationMap[field] || {};
                const entries = Object.entries(legend);
                return (
                  <div key={field} style={{ marginBottom: 20 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                      <div style={{ width: 3, height: 16, background: '#0052CC', borderRadius: 2 }} />
                      <span style={{ fontSize: 12, fontWeight: 600, color: '#172B4D' }}>Anonymized — {field}</span>
                      <span style={{ fontSize: 11, color: '#5E6C84' }}>— values replaced with stable pseudonyms. AI sees the pseudonym, not the real value.</span>
                    </div>
                    {entries.length === 0 ? (
                      <div style={{ fontSize: 12, color: '#5E6C84', fontStyle: 'italic' }}>No values encountered in sample.</div>
                    ) : (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                        {entries.map(([pseudo, real]) => (
                          <div key={pseudo} style={{
                            display: 'flex', alignItems: 'stretch',
                            border: '1px solid #B3D4FF', borderRadius: 4, overflow: 'hidden',
                            fontSize: 12, minWidth: 200, maxWidth: 340,
                          }}>
                            <div style={{ background: '#E9F2FF', color: '#0052CC', fontWeight: 600, padding: '5px 10px', whiteSpace: 'nowrap', borderRight: '1px solid #B3D4FF', minWidth: 110 }}>
                              {pseudo}
                            </div>
                            <div style={{ background: '#fff', color: '#172B4D', padding: '5px 10px', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {real}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </>
          )}
        </div>
      )}

      {activePane === 'Sample items sent to AI' && (
        <div style={{ padding: 14 }}>
          <div style={{ display: 'flex', gap: 16, marginBottom: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <div style={{ display: 'flex', gap: 10, fontSize: 11 }}>
              {[
                { bg: '#F4F5F7', border: '#DFE1E6', label: 'Normal' },
                { bg: '#EEF4FF', border: '#B3D4FF', label: 'Anonymized (pseudonym shown)' },
                { bg: '#FFF0EC', border: '#FF8F73', label: 'Excluded (not sent)' },
              ].map(({ bg, border, label }) => (
                <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 5, color: '#5E6C84' }}>
                  <div style={{ width: 12, height: 12, background: bg, border: `1px solid ${border}`, borderRadius: 2, flexShrink: 0 }} />
                  {label}
                </div>
              ))}
            </div>
            <div style={{ marginLeft: 'auto', fontSize: 12, color: '#5E6C84' }}>
              {sampleItems.length} items sent
              {totalItems > 0 && ` of ${totalItems} total (${Math.round(sampleItems.length / totalItems * 100)}%)`}
              {effectiveExcludedFields.length > 0 && ` · ${effectiveExcludedFields.length} field${effectiveExcludedFields.length > 1 ? 's' : ''} excluded`}
              {effectiveAnonymizedFields.length > 0 && ` · ${effectiveAnonymizedFields.length} field${effectiveAnonymizedFields.length > 1 ? 's' : ''} anonymized`}
            </div>
          </div>

          {sampleItems.length === 0 ? (
            <div style={{ fontSize: 13, color: '#5E6C84', padding: '20px 0', textAlign: 'center' }}>
              No sample items available. Generate an assessment first.
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ borderCollapse: 'collapse', fontSize: 11, whiteSpace: 'nowrap', width: '100%' }}>
                <thead>
                  <tr>
                    {sortedColumns.map(col => {
                      const h = colHeader(col);
                      return (
                        <th key={col} style={{
                          padding: '5px 8px', border: '1px solid #DFE1E6',
                          background: h.bg, color: h.color, fontWeight: 500, textAlign: 'left',
                        }}>
                          {col}
                          {h.badge && (
                            <span style={{
                              marginLeft: 4, fontSize: 9, fontWeight: 700,
                              background: h.badgeBg, color: h.badgeColor,
                              borderRadius: 2, padding: '1px 3px', verticalAlign: 'middle',
                            }}>
                              {h.badge}
                            </span>
                          )}
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {sampleItems.slice(0, 8).map((row, i) => (
                    <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#FAFAFA' }}>
                      {sortedColumns.map(col => {
                        const { val, style } = cellVal(row, col);
                        return (
                          <td key={col} style={{ padding: '4px 8px', border: '1px solid #DFE1E6', ...style }}>
                            {val}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                  {sampleItems.length > 8 && (
                    <tr>
                      <td colSpan={sortedColumns.length} style={{ padding: '5px 8px', border: '1px solid #DFE1E6', color: '#5E6C84', fontStyle: 'italic', fontSize: 11 }}>
                        + {sampleItems.length - 8} more rows sent to AI (not shown)
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function AiDataButton({ onClick, excludedCount = 0, anonymizedCount = 0, isActive = false }) {
  const total = excludedCount + anonymizedCount;
  return (
    <button
      onClick={onClick}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        padding: '5px 10px',
        borderRadius: 4,
        border: isActive ? '1px solid #0052CC' : '1px solid #DFE1E6',
        background: isActive ? '#EEF4FF' : '#FFF8E6',
        color: isActive ? '#0052CC' : '#854F0B',
        fontSize: 12,
        fontWeight: 600,
        cursor: 'pointer',
        whiteSpace: 'nowrap',
      }}
    >
      <span style={{ fontSize: 14, lineHeight: 1 }}>🔒</span>
      AI Data
      {total > 0 && (
        <span style={{
          fontSize: 10, fontWeight: 700,
          background: '#F0C070', color: '#633806',
          borderRadius: 3, padding: '1px 5px', lineHeight: '14px',
        }}>
          {total}
        </span>
      )}
    </button>
  );
}
